import React, { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { PICKER_PANEL_MAX_W } from './fieldPickerChrome';

/**
 * Reusable Modal Component with React Portal
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal should close
 * @param {string} title - Modal title
 * @param {string} subtitle - Optional subtitle text
 * @param {React.ReactNode} children - Modal content
 * @param {React.ReactNode} footer - Optional custom footer (overrides default buttons)
 * @param {string} size - Modal size: 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * @param {boolean} showCloseButton - Show X button in header (default: true)
 * @param {boolean} closeOnOverlayClick - Close when clicking backdrop (default: true)
 * @param {boolean} closeOnEscape - Close on Escape key (default: true)
 * @param {string} className - Additional classes for modal content
 * @param {boolean} noPadding - When true, body container omits its p-5 padding (default: false)
 * @param {string} backdropClassName - Extra classes appended to the backdrop overlay
 *   (e.g. `backdrop-blur-sm`). Opt-in — blur was globally removed for perf, so
 *   callers must explicitly request it where the heavier effect is acceptable.
 */

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  headerActions,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  noPadding = false,
  backdropClassName = '',
}) => {
  const titleId = useId();
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Save previously focused element and move focus into dialog on open.
  // If a child already placed focus inside (e.g. <input autoFocus />), respect
  // it — otherwise picking focusable[0] would steal focus from the field the
  // consumer intentionally flagged, and Enter would fire on the Close X button.
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocused.current = document.activeElement;
    const frame = requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (dialog.contains(document.activeElement) && document.activeElement !== dialog) {
        return;
      }
      const focusable = dialog.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        dialog.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  // Restore focus on close
  useEffect(() => {
    if (isOpen) return;
    const el = previouslyFocused.current;
    if (el && document.contains(el)) {
      el.focus();
    }
  }, [isOpen]);

  // Tab / Shift+Tab focus trap
  useEffect(() => {
    if (!isOpen) return;
    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    // Field pickers share one width across every shell that hosts one — the
    // value lives in common/fieldPickerChrome so it can't drift per call site.
    picker: PICKER_PANEL_MAX_W,
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
  };

  const modalContent = (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 animate-fade-in text-left ${backdropClassName}`}
      onClick={(e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={`
          bg-white dark:bg-slate-900 grey:bg-zinc-50
          rounded-xl w-full ${sizeClasses[size]} max-h-[90vh]
          shadow-[0_20px_50px_-12px_rgba(15,23,42,0.25)] dark:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] grey:shadow-[0_20px_50px_-12px_rgba(15,23,42,0.2)]
          animate-scale-in border border-slate-100 dark:border-slate-800 grey:border-zinc-300
          transition-colors duration-300
          flex flex-col overflow-hidden
          focus:outline-none
          ${className}
        `}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800 grey:border-zinc-300 flex-shrink-0">
            <div className="flex-1 min-w-0">
              {title && (
                <h3 id={titleId} className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-200 grey:text-zinc-900">
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-xs text-slate-500 dark:text-slate-400 grey:text-zinc-600 mt-1.5 leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>
            {headerActions && (
              <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                {headerActions}
              </div>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="ml-2 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 grey:hover:text-zinc-600 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 grey:hover:bg-zinc-200"
                aria-label="Close"
                data-tooltip-content="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={`flex-1 overflow-y-auto min-h-0 ${noPadding ? '' : 'p-5'}`}>{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-slate-800 grey:border-zinc-300 bg-slate-50 dark:bg-slate-900/50 grey:bg-zinc-100 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Use Portal to render modal at document.body level
  return createPortal(modalContent, document.body);
};

export default Modal;

/**
 * Convenience component for simple confirm/cancel modals
 */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary', // 'primary' | 'danger'
  isLoading = false,
  ...modalProps
}) => {
  const confirmButtonClass =
    confirmVariant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 text-white'
      : 'bg-indigo-600 hover:bg-indigo-700 text-white';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      showCloseButton={false}
      {...modalProps}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 grey:text-zinc-600 font-bold hover:bg-slate-100 dark:hover:bg-slate-800 grey:hover:bg-zinc-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-3 py-1.5 text-sm font-bold rounded-lg active:scale-95 transition-all flex items-center disabled:opacity-50 disabled:grayscale disabled:scale-100 ${confirmButtonClass}`}
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </>
      }
    >
      {message && (
        <p className="text-sm text-slate-500 dark:text-slate-400 grey:text-zinc-600">
          {message}
        </p>
      )}
    </Modal>
  );
};

/**
 * Convenience component for form modals
 */
export const FormModal = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  subtitle,
  submitText = 'Submit',
  cancelText = 'Cancel',
  isLoading = false,
  submitDisabled = false,
  richFooter = false,
  loadingText = 'Creating…',
  children,
  ...modalProps
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.();
  };

  // Two footer styles. Default (lightweight px-3 py-1.5) is shared by ~12
  // consumers app-wide and stays byte-identical. `richFooter` opts into the
  // larger px-4 py-2 buttons + inline spinner used by the NavigationTree
  // creation modals.
  const footerButtons = richFooter ? (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-400 grey:text-zinc-600 hover:bg-slate-100 dark:hover:bg-slate-800 grey:hover:bg-zinc-300 rounded-lg transition-colors disabled:opacity-50"
      >
        {cancelText}
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isLoading || submitDisabled}
        className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-lg shadow-indigo-200 dark:shadow-indigo-900/20 grey:shadow-indigo-200 disabled:opacity-50 disabled:shadow-none transition-all flex items-center"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {loadingText}
          </>
        ) : (
          submitText
        )}
      </button>
    </>
  ) : (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading}
        className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 grey:text-zinc-600 font-bold hover:bg-slate-100 dark:hover:bg-slate-800 grey:hover:bg-zinc-200 rounded-lg transition-colors disabled:opacity-50"
      >
        {cancelText}
      </button>
      <button
        type="submit"
        onClick={handleSubmit}
        disabled={isLoading || submitDisabled}
        className="px-3 py-1.5 text-sm bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 active:scale-95 transition-all flex items-center disabled:opacity-50 disabled:grayscale disabled:scale-100"
      >
        {isLoading ? 'Processing...' : submitText}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      {...modalProps}
      footer={footerButtons}
    >
      <form onSubmit={handleSubmit}>
        {children}
        {/* Hidden submit input — makes Enter-in-input fire the form's onSubmit
            even though the visible Submit button lives in the footer, outside
            the <form>. Without this, multi-input FormModals silently ignore
            Enter because the HTML "implicit submit" rule needs a button in-form. */}
        <button type="submit" aria-hidden="true" tabIndex={-1} className="hidden" />
      </form>
    </Modal>
  );
};
